import * as React from 'react';
import classNames from 'classnames';
import {
  ISlideShowGalleryImperativeActions,
  SlideShowGallerySkinProps,
} from '../SlideShowGallery.types';
import { TestIds, translations } from '../constants';
import { PauseEvent, PlayEvent } from '../../../core/corvid/props-factories';
import { useGesture } from '../../../providers/useGesture/useGesture';
import { useInViewport } from '../../../providers/useInViewport/useInViewport';
import { useInterval } from '../../../providers/useInterval/useInterval';
import SlideShowGalleryItem from '../../GalleryItem/SlideShowGalleryItem';
import { keyCodes } from '../../../core/commons/a11y';
import SlideShowGalleryItemContainer from './SlideShowGalleryItemContainer';
import { NavigationButtons } from './NavigationButtons';
import { GalleryCounter } from './GalleryCounter';

const NO_TRANSITION = 'NoTransition';

const emptyFn = () => {};

const SlideShowGallery: React.ForwardRefRenderFunction<
  ISlideShowGalleryImperativeActions,
  SlideShowGallerySkinProps
> = (props, ref) => {
  const {
    id,
    items,
    skinsStyle,
    skinsItemStyle,
    currentIndex,
    setCurrentIndex,
    onCurrentItemChanged,
    overlay,
    autoPlay,
    autoPlayInterval,
    setAutoPlay,
    reducedMotion,
    transition,
    transitionDuration,
    shouldPreserveAspectRatio,
    openImageZoom = emptyFn,
    onMouseEnter = emptyFn,
    onMouseLeave = emptyFn,
    imageOnClickAction,
    showAutoplay,
    showNavigation,
    translate,
    onNavigationEnd,
    play,
    pause,
    onPlay = emptyFn,
    onPause = emptyFn,
    isPlaying: canPlay = autoPlay && items.length > 1 && !reducedMotion,
    reverse = props.transitionReverse,
    alignTextRight,
    onItemClicked,
    renderNavigationButtonsInHelpers = false,
  } = props;
  const galleryRef = React.useRef<HTMLDivElement>(null);
  const isGalleryInViewport = useInViewport(galleryRef);
  const isPlaying = canPlay && isGalleryInViewport;
  const [inTransition, setInTransition] = React.useState(false);
  const hasTransition = transition !== NO_TRANSITION;
  const currentItem = items[currentIndex];
  const [navigationReverse, setNavigationReverse] = React.useState(false);

  const setBackwardDirection = () => setNavigationReverse(true);
  const setForwardDirection = () => setNavigationReverse(false);

  const moveToItemImmediate = React.useCallback(
    (newItemIndex: number) => {
      setCurrentIndex(newItemIndex);
      onCurrentItemChanged?.({
        type: 'imageChanged',
        item: currentItem,
        itemIndex: newItemIndex,
      });

      if (onNavigationEnd) {
        onNavigationEnd(newItemIndex);
      }
    },
    [setCurrentIndex, onCurrentItemChanged, currentItem, onNavigationEnd],
  );

  const moveToItemWithTransition = React.useCallback(
    (newItemIndex: number) => {
      if (inTransition) {
        return;
      }

      setInTransition(true);
      setCurrentIndex(newItemIndex);
      onCurrentItemChanged?.({
        type: 'imageChanged',
        item: currentItem,
        itemIndex: newItemIndex,
      });
    },
    [inTransition, setCurrentIndex, currentItem, onCurrentItemChanged],
  );

  const onTransitionEnd = React.useCallback(() => {
    setInTransition(false);

    if (onNavigationEnd) {
      onNavigationEnd(currentIndex);
    }
  }, [currentIndex, setInTransition, onNavigationEnd]);

  const moveToItem = React.useCallback(
    (newItemIndex: number) =>
      hasTransition
        ? moveToItemWithTransition(newItemIndex)
        : moveToItemImmediate(newItemIndex),
    [moveToItemImmediate, moveToItemWithTransition, hasTransition],
  );

  const moveToNextItem = React.useCallback(() => {
    const newItemIndex =
      currentIndex === items.length - 1 ? 0 : currentIndex + 1;

    setForwardDirection();
    return moveToItem(newItemIndex);
  }, [items.length, currentIndex, moveToItem]);

  const moveToPrevItem = React.useCallback(() => {
    const newItemIndex =
      currentIndex === 0 ? items.length - 1 : currentIndex - 1;

    setBackwardDirection();
    return moveToItem(newItemIndex);
  }, [items.length, currentIndex, moveToItem]);

  const handleNavigationByKey: React.KeyboardEventHandler<HTMLDivElement> = event => {
    if (event.keyCode === keyCodes.arrowRight) {
      moveToNextItem();
    } else if (event.keyCode === keyCodes.arrowLeft) {
      moveToPrevItem();
    }
  };

  const togglePlay = () => {
    // user clicked pause
    if (autoPlay) {
      onPause({ type: 'autoplayOff' } as PauseEvent);
      // user clicked play
    } else {
      onPlay({ type: 'autoplayOn' } as PlayEvent);
    }
    setAutoPlay(!autoPlay);
  };

  const itemClickHandler = () => {
    onItemClicked?.({
      itemIndex: currentIndex,
      type: 'itemClicked',
    });
  };

  useGesture(
    'onSwipeLeft',
    reverse ? moveToPrevItem : moveToNextItem,
    galleryRef,
  );
  useGesture(
    'onSwipeRight',
    reverse ? moveToNextItem : moveToPrevItem,
    galleryRef,
  );
  useInterval(moveToNextItem, isPlaying ? autoPlayInterval : null);

  React.useImperativeHandle(
    ref,
    () => {
      return {
        next: moveToNextItem,
        previous: moveToPrevItem,
        play: () => {
          play();
          onPlay?.({ type: 'autoplayOn' } as PlayEvent);
        },
        pause: () => {
          pause();
          onPause?.({ type: 'autoplayOff' } as PauseEvent);
        },
      };
    },
    [moveToNextItem, moveToPrevItem, onPlay, onPause, play, pause],
  );

  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      id={id}
      data-testid={TestIds.root}
      ref={galleryRef}
      role="region"
      aria-label={translate!(
        translations.ARIA_LABEL_NAMESPACE,
        translations.SLIDE_SHOW_GALLERY_ARIA_LABEL_KEY,
        translations.SLIDE_SHOW_GALLERY_ARIA_LABEL_DEFAULT,
      )}
      className={classNames(skinsStyle.root, {
        [skinsStyle.flexibleHeight]: shouldPreserveAspectRatio,
        [skinsStyle.textAlignmentRight]: alignTextRight,
      })}
      onKeyDown={handleNavigationByKey}
    >
      {showNavigation && !renderNavigationButtonsInHelpers && (
        <NavigationButtons
          translate={translate}
          skinsStyle={skinsStyle}
          moveToPrevItem={moveToPrevItem}
          moveToNextItem={moveToNextItem}
          reverse={reverse}
          withCounter={false}
        />
      )}
      <SlideShowGalleryItemContainer
        skinsStyle={skinsStyle}
        currentIndex={currentIndex}
        isPlaying={isPlaying}
        transition={transition}
        transitionDuration={transitionDuration}
        reverse={navigationReverse}
        onTransitionEnd={onTransitionEnd}
      >
        <SlideShowGalleryItem
          itemClickHandler={itemClickHandler}
          skinsStyle={skinsItemStyle}
          extraClassNames={{ root: skinsStyle.item, panel: skinsStyle.panel }}
          shouldPreserveAspectRatio={shouldPreserveAspectRatio}
          imageOnClickAction={imageOnClickAction}
          openImageZoom={openImageZoom}
          translate={translate}
          alignTextRight={alignTextRight}
          {...currentItem}
        />
      </SlideShowGalleryItemContainer>
      <div className={skinsStyle.helpers}>
        {showAutoplay && (
          <button
            className={classNames(
              skinsStyle.playButton,
              isPlaying ? skinsStyle.autoplayOn : skinsStyle.autoplayOff,
            )}
            data-testid={TestIds.playButton}
            type="button"
            aria-label={translate!(
              translations.ARIA_LABEL_NAMESPACE,
              translations.PLAY_BUTTON_ARIA_LABEL_KEY,
              translations.PLAY_BUTTON_ARIA_LABEL_DEFAULT,
            )}
            aria-pressed={isPlaying}
            onClick={togglePlay}
          >
            <span
              className={skinsStyle.playIcon}
              data-testid={TestIds.playButtonInner}
              role="presentation"
              aria-hidden="true"
            />
          </button>
        )}
        {showNavigation && renderNavigationButtonsInHelpers ? (
          <NavigationButtons
            translate={translate}
            skinsStyle={skinsStyle}
            moveToPrevItem={moveToPrevItem}
            moveToNextItem={moveToNextItem}
            reverse={reverse}
            withCounter={true}
            currentIndex={currentIndex}
            itemsLength={items.length}
          />
        ) : (
          <GalleryCounter
            skinsStyle={skinsStyle}
            currentIndex={currentIndex}
            itemsLength={items.length}
          />
        )}
      </div>
      {overlay}
    </div>
  );
};

export default React.forwardRef(SlideShowGallery);
